import { FC, forwardRef } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import en from 'date-fns/locale/en-US'
import th from 'date-fns/locale/th'
import { format } from 'date-fns'
import range from 'lodash/range'
import { standardSizeInput } from './utils'
import { useMonth } from '../hooks/useMonth'
import { RootStateType } from '../redux/reducer/rootReducer'
import Typography from '@mui/material/Typography'
import { FormHelperText } from '@mui/material'
import { theme } from '../constant/Theme'

export type ValueDatetime = Date | null
type Size = 'small' | 'medium' | 'large'
export interface DatePickerProps {
    onChange?: (value: ValueDatetime) => void
    size?: Size
    label?: string
    name: string
    required?: boolean
    locale?: 'th' | 'en'
    value?: ValueDatetime
    error?: boolean
    disabled?: boolean
    helperText?: string
    minDate?: Date
    maxDate?: Date
    endYear?: number
    rageYear?: 'past' | 'future'
}

const DatesPicker: FC<DatePickerProps> = ({
    onChange,
    size = 'small',
    locale = 'th',
    value,
    label,
    name,
    minDate,
    maxDate,
    endYear = 5,
    rageYear,
    helperText = 'Required',
    required = false,
    disabled = false,
    error = false,
}) => {
    const language = useSelector(({ language }: RootStateType) => language)
    const localeDate = locale === 'th' ? th : en
    const { t } = useTranslation()
    const [month] = useMonth()

    return (
        <DivStyle
            style={{
                width: '100%',
                fontFamily: 'Prompt_Light',
            }}
            error={error}
            name={name}
        >
            <Typography>
                {label} {!disabled && required && <label style={{ color: '#d32f2f' }}>{' *'}</label>}
            </Typography>
            <DatetimePickerStyle disabled={disabled} size={size}>
                <DatePicker
                    selected={value}
                    locale={localeDate}
                    dateFormat="yyyy-MM-dd"
                    onChange={(valueDT: ValueDatetime) => {
                        onChange && onChange(valueDT)
                    }}
                    renderCustomHeader={(props) => (
                        <CustomHeader
                            {...props}
                            locale={locale}
                            localeDate={localeDate}
                            month={month}
                            language={language}
                            endYear={endYear}
                            rageYear={rageYear}
                        />
                    )}
                    customInput={<CustomInput error={error} disabled={disabled} locale={locale} size={size} />}
                    wrapperClassName="wrapper"
                    isClearable={true}
                    disabled={disabled}
                    minDate={minDate}
                    maxDate={maxDate}
                />
            </DatetimePickerStyle>
            {!disabled && required && error && (
                <FormHelperText>
                    <label style={{ color: '#d32f2f', marginLeft: '14px' }}>
                        {helperText ? helperText : t('LABEL.SELECT_REQUIRED')}
                    </label>
                </FormHelperText>
            )}
        </DivStyle>
    )
}

interface CustomHeaderProps {
    language: 'th' | 'en'
    month: {
        id: string
        name: {
            th: string
            en: string
        }
    }[]
    endYear?: number
    changeMonth: (month: number) => void
    changeYear?: (year: number) => void
    decreaseMonth: () => void
    increaseMonth: () => void
    date: Date
    prevMonthButtonDisabled: boolean
    nextMonthButtonDisabled: boolean
    locale: string
    localeDate: Locale
    rageYear?: 'past' | 'future'
}

const CustomHeader = ({ changeMonth, changeYear, language, date, month, endYear, rageYear }: CustomHeaderProps) => {
    let year =
        rageYear === 'past' && endYear
            ? range(new Date().getFullYear(), new Date().getFullYear() - endYear, -1)
            : rageYear === 'past'
            ? range(new Date().getFullYear(), new Date().getFullYear() - 50, -1)
            : rageYear === 'future' && endYear
            ? range(new Date().getFullYear(), new Date().getFullYear() + endYear, 1)
            : range(new Date().getFullYear(), new Date().getFullYear() - 90, -1)

    return (
        <div
            style={{
                margin: 10,
                display: 'flex',
                justifyContent: 'center',
                fontFamily: 'Prompt_Light',
            }}
        >
            <div style={{ width: '100%' }}>
                <select
                    style={{ fontFamily: 'Prompt_Light' }}
                    defaultValue={month[date.getMonth()]?.id}
                    value={month[date.getMonth()]?.id}
                    onChange={({ target: { value } }) => {
                        changeMonth?.(Number(value) - 1)
                    }}
                >
                    {month.map((option) => (
                        <option key={option.id} value={option.id}>
                            {option.name.th}
                        </option>
                    ))}
                </select>

                <select
                    style={{ fontFamily: 'Prompt_Light' }}
                    value={date.getFullYear()}
                    onChange={({ target: { value } }) => {
                        changeYear?.(Number(value))
                    }}
                >
                    {year.map((option) => (
                        <option key={option} value={option}>
                            {language === 'th' ? option + 543 : option}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    )
}

interface CustomInputProps {
    onClick?: () => void
    value?: string
    error?: boolean
    disabled?: boolean
    locale?: string
    size?: Size
}
const CustomInput = forwardRef(
    ({ onClick, value, error = false, disabled = false, locale = 'th', size = 'small' }: CustomInputProps, ref) => {
        const newFormat = value?.slice(0, 10)
        const getYear = new Date(`${newFormat}`).getFullYear()
        const reg = new RegExp(getYear.toString(), 'g')
        const replace = locale === 'th' ? value?.replace(reg, (getYear + 543).toString()) : value
        const displayValue = value ? format(new Date(`${replace}`.replace(/\s+/g, 'T')), 'dd/MM/yyyy') : ''
        return (
            <CustomInputStyle type="button" onClick={onClick} ref={ref} error={error} disabled={disabled} size={size}>
                {displayValue}
            </CustomInputStyle>
        )
    },
)

const errorColor = '#ff0000'
const normalColor = '#b3b3b3'
const defaultColor = '#318AF1'
const blackColor = '#000000'

const heightFc = (size: Size) => {
    switch (size) {
        case 'small':
            return standardSizeInput.small
        case 'medium':
            return standardSizeInput.medium
        default:
            return standardSizeInput.large
    }
}

const borderFc = (props: { error: boolean; disabled: boolean }) => {
    if (props.error) {
        return errorColor
    }

    if (props.disabled) {
        return normalColor
    }

    return blackColor
}

const sizeFc = (size: Size) => {
    switch (size) {
        case 'medium':
            return '2.5rem'
        case 'large':
            return '3.5rem'

        default:
            return '1.5rem'
    }
}

const CustomInputStyle = styled.button<any>`
    font-family: Prompt_Light;
    height: ${(props) => heightFc(props.size)}px;
    width: 100%;
    background-color: #ffffff;
    border: 1px solid ${(props) => (props.error ? errorColor : normalColor)};
    outline: unset;
    border-radius: 5px;
    text-align: start;
    :hover {
        border-color: ${(props) => borderFc(props)};
    }
    :focus {
        border-color: ${(props) => borderFc(props)};
    }
`

const DivStyle = styled.div<any>`
    div {
        label {
            color: ${(props) => {
                if (props.error) return errorColor
                else return '#696969'
            }};
        }
    }
`

const DatetimePickerStyle = styled.div<any>`
    font-family: Prompt_Light;
    height: ${(props) => heightFc(props.size)}px !important;
    .wrapper {
        width: 100%;
        height: ${(props) => heightFc(props.size)}px;
    }
    div.react-datepicker__day-names {
        font-family: Prompt_Light;
    }
    div.react-datepicker__month {
        font-family: Prompt_Light;
    }
    div.react-datepicker-time__input {
        border: unset;
        outline: unset;
    }
    input.react-datepicker-time__input {
        border: 5px solid #b3b3b3;
        border-radius: 5px;
        width: 100% !important;
        height: 30px;
        :hover {
            outline: unset;
            border: 5px solid ${defaultColor};
        }
        :focus {
            outline: unset;
            border: 5px solid ${defaultColor};
        }
    }
    input[type='time']::-webkit-calendar-picker-indicator {
        filter: invert(36%) sepia(78%) saturate(5055%) hue-rotate(224deg) brightness(94%) contrast(92%);
        outline: unset;
    }
    .react-datepicker__header {
        font-family: Prompt_Light;
        background-color: ${defaultColor};
        color: #ffffff;
        .react-datepicker__day-name {
            color: #ffffff;
        }
    }
    .react-datepicker__day--selected {
        outline: unset;
        background-color: ${defaultColor};
        border-radius: 50%;
    }
    .react-datepicker__day--keyboard-selected {
        background-color: unset;
        color: #000000;
    }
    .react-datepicker__close-icon::after {
        background-color: unset;
        color: ${normalColor};
        font-size: 18px;
        text-align: center;
        width: 1.5rem;
        padding: unset;
        display: unset;
    }
    .react-datepicker__close-icon {
        display: ${(props) => (props.disabled ? 'none' : 'block')};
        text-align: right;
        border-radius: 50%;
        width: ${(props) => sizeFc(props.size)};
        right: 0.5rem;
        height: ${(props) => sizeFc(props.size)};
        top: 0.6rem;
        :hover {
            background-color: #cec2c230;
        }
    }
`

export default DatesPicker
